import { Box, FormControl, MenuItem, Select, TextField } from "@mui/material";
import FilterGridRow from "./scheduler/FilterGridRow";
import { sortBy } from "lodash";
import { useOrgConfig, OrgConfig } from "~/providers/OrgConfigProvider";
import { calculateDuration, getServiceDefaults } from "~/utils/visitsHelper";
import CheckboxSelect from "./common/forms/CheckboxSelect";

type VistTypeInput = {
  duration: string,
  durationLocked: boolean,
  visitTypeId: string,
  serviceIds: string[],
  serviceNames: string[],
}

type Props = {
  values: VistTypeInput
  setValues: (values: VistTypeInput) => void
  serviceLineId: string
}

export const setVisitType = (visitTypeId: string, values: VistTypeInput, orgConfig: OrgConfig): VistTypeInput => {
  // Change to the default selected services for the new visit type
  const newVisitType = orgConfig.visitTypes[visitTypeId];
  const serviceIds = getServiceDefaults(orgConfig, parseInt(visitTypeId));

  return updateDuration({
    ...values,
    visitTypeId,
    serviceIds,
    serviceNames: serviceIds.map(sid => newVisitType?.visitTypeServices.find(s => s.service?.id.toString() === sid)?.service?.name || ""),
  }, orgConfig)
}

export const updateDuration = (values: VistTypeInput, orgConfig: OrgConfig): VistTypeInput => {
  if (values.durationLocked) { return values; }

  return {
    ...values,
    duration: calculateDuration(orgConfig, parseInt(values.visitTypeId), values.serviceIds),
  };
}

const VisitTypeDuration = ({ values, setValues, serviceLineId }: Props) => {
  const orgConfig = useOrgConfig()
  const serviceLine = orgConfig.serviceLines[serviceLineId];
  const visitType = orgConfig.visitTypes[values.visitTypeId];
  const visitTypeOptions = sortBy(
    (serviceLine?.visitTypeServiceLines || [])
      .map(vtsl => orgConfig.visitTypes[vtsl.visitTypeId.toString()])
      .filter((vt): vt is NonNullable<typeof vt> => !!vt),
    "name"
  );

  return <>
    <FilterGridRow label="Visit Type" loading={orgConfig.loading}>
      <FormControl variant="standard" fullWidth>
      <Select
        fullWidth
        value={values.visitTypeId}
        displayEmpty
        renderValue={(selected) => {
          if (!selected) {
            return (
              <Box component="span" sx={{ color: "text.disabled" }}>
                Select visit type
              </Box>
            );
          }
          return orgConfig.visitTypes[selected]?.name ?? selected;
        }}
        onChange={(e) => {
          setValues(setVisitType(e.target.value, values, orgConfig))
        }}
      >
        <MenuItem value="" disabled>
          Select visit type
        </MenuItem>
        {visitTypeOptions.map(vt => (
          <MenuItem key={vt.id} value={vt.id.toString()}>
            {vt.name}
          </MenuItem>
        ))}
      </Select>
      </FormControl>
    </FilterGridRow>

    {!!visitType?.visitTypeServices.length ? (
      <FilterGridRow label="Services" loading={orgConfig.loading}>
        <CheckboxSelect
          value={values.serviceIds.map(sid => parseInt(sid))}
          onChange={serviceIds => setValues(updateDuration({
              ...values,
              serviceIds: serviceIds.map(sid => sid.toString()),
              serviceNames: serviceIds.map(sid => visitType.visitTypeServices.find(s => s && (s.service?.id === sid))?.service?.name ?? ""),
            }, orgConfig))}
          options={visitType.visitTypeServices.map(({ service }) => ({ key: service?.id || 0, label: service?.name || '' }))}
          sx={{ minWidth: "18em" }}
          fullWidth
          size="small"
        />
      </FilterGridRow>
    ) : null}

    <FilterGridRow label="Duration" loading={orgConfig.loading}>
      <FormControl variant="standard" fullWidth>
        <TextField
          variant="standard"
          fullWidth
          value={values.duration}
          onChange={(e) => setValues({ ...values, duration: e.target.value, durationLocked: true })}
        />
      </FormControl>
    </FilterGridRow>
  </>
}

export default VisitTypeDuration
